import { ImageOff, ImagePlus, Pencil } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useFirstMedia } from "../../../hooks/media/useFirstMedia";
import {
  MediaItemCategory,
  MediaItemCategoryString,
} from "../../../models/MediaItemCategory";
import { IUploadMediaDefaults } from "../ModalUploadMedia";

export interface IMediaCategorySectionProps {
  locationId: string | undefined;
  label: string;
  category: MediaItemCategory;
  onUploadClick?: (defaults: IUploadMediaDefaults) => void;
}

export const MediaCategorySection: React.FunctionComponent<
  IMediaCategorySectionProps
> = ({ locationId, label, category, onUploadClick }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`mediaPage.${key}`);

  const categoryString: string = MediaItemCategoryString[category];
  const { data: media, isLoading } = useFirstMedia({
    locationId,
    category: categoryString,
  });

  const handleClick = () => {
    if (isLoading) return;
    onUploadClick?.({ category });
  };

  // Forces browser to re-fetch the image after updating
  // (The media URL does not change, but the content does, so we add a dummy query parameter to bypass cache)
  const mediaUrlModifiedParameter = media?.url?.includes("?")
    ? `&modified=${media?.last_modified_at}`
    : `?modified=${media?.last_modified_at}`;

  const mediaUrl = media?.url
    ? `${media.url}${mediaUrlModifiedParameter}`
    : null;

  const cardContent = isLoading ? (
    <div className="media-category-placeholder" />
  ) : mediaUrl ? (
    <>
      <img
        className="media-category-img"
        src={mediaUrl}
        alt={label}
        referrerPolicy="no-referrer"
      />
      {onUploadClick && (
        <div className="media-category-change-overlay">
          <Pencil size={14} />
          {t("change")}
        </div>
      )}
    </>
  ) : onUploadClick ? (
    <div className="media-category-upload-prompt">
      <ImagePlus size={40} strokeWidth={1.5} />
      <span>{t("upload_image")}</span>
    </div>
  ) : (
    <div className="media-category-upload-prompt">
      <ImageOff size={40} strokeWidth={1.5} />
      <span>{t("not_uploaded")}</span>
    </div>
  );

  return (
    <div className="media-category-section">
      <div className="section-title">{label}</div>
      {onUploadClick ? (
        <button
          className={`media-category-card ${media ? "has-image" : "no-image"}`}
          onClick={handleClick}
          disabled={isLoading}
        >
          {cardContent}
        </button>
      ) : mediaUrl ? (
        <a
          className={`media-category-card has-image`}
          href={mediaUrl}
          target="_blank"
          rel="noreferrer"
        >
          {cardContent}
        </a>
      ) : (
        <div className="media-category-card no-image read-only">
          {cardContent}
        </div>
      )}
    </div>
  );
};
